import { useEffect, useState, useSyncExternalStore } from 'react';
import {
  Button,
  Group,
  Loader,
  Progress,
  SegmentedControl,
  Select,
  Stack,
  Text,
} from '@mantine/core';
import { WidgetFrame, type WidgetProps } from '@ayocore/platform/client/widgets';
import { useAuthController } from '@ayocore/platform/identity/client/useAuthController';
import { useUserSetting } from '@ayocore/platform/identity/client/settings/useUserSetting';
import { getLeadFunnel } from '../../../client/rpc';
import { crmWidgetStyle } from '../../../client/components/Widgets/shared/presentation';
import { DEFAULT_LEAD_FUNNEL_SETTINGS, decodeLeadFunnelSettings } from '../common/settings';
import { LeadFunnelWidgetController } from './LeadFunnelWidgetController';

const percent = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 });
const count = new Intl.NumberFormat('ru-RU');

export function LeadFunnelWidget(props: WidgetProps) {
  const userId = useAuthController(state => state.user?.id);
  return userId == null ? null : <UserLeadFunnel key={userId} {...props} />;
}

function UserLeadFunnel({ title = 'Лиды', className }: WidgetProps) {
  const preferences = useUserSetting('crm.widgets.lead-funnel', {
    defaultValue: DEFAULT_LEAD_FUNNEL_SETTINGS,
    decode: decodeLeadFunnelSettings,
  });
  const [controller] = useState(() => new LeadFunnelWidgetController(getLeadFunnel));
  const { data, loading, error } = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot
  );
  useEffect(() => {
    if (preferences.ready) void controller.load(preferences.value);
    return () => controller.cancelLoad();
  }, [controller, preferences.ready, preferences.value]);
  return (
    <WidgetFrame
      title={title}
      className={className}
      radius='lg'
      style={{ ...crmWidgetStyle, height: '100%', minWidth: 0 }}
    >
      <SegmentedControl
        size='xs'
        aria-label='Область данных воронки лидов'
        value={preferences.value.scope}
        disabled={!preferences.ready || preferences.saving}
        data={[
          { label: 'Мои', value: 'mine' },
          { label: 'Филиал', value: 'branch' },
          { label: 'Все', value: 'all' },
        ]}
        onChange={scope => {
          if (scope === 'mine' || scope === 'branch' || scope === 'all')
            void preferences.setValue({ ...preferences.value, scope });
        }}
      />
      {data && data.types.length > 1 && (
        <Select
          aria-label='Воронка лидов'
          placeholder='Выберите воронку'
          size='xs'
          allowDeselect={false}
          disabled={preferences.saving}
          value={data.selectedTypeId == null ? null : String(data.selectedTypeId)}
          data={data.types.map(type => ({ value: String(type.id), label: type.name }))}
          onChange={value => {
            if (value)
              void preferences.setValue({ ...preferences.value, leadTypeId: Number(value) });
          }}
        />
      )}
      {data && data.types.length === 1 && (
        <Text size='xs' c='dimmed'>
          {data.types[0]?.name}
        </Text>
      )}
      {preferences.error && (
        <Stack gap='xs'>
          <Text role='alert' c='red' size='sm'>
            {preferences.error}
          </Text>
          {!preferences.ready && (
            <Button size='xs' variant='light' onClick={() => void preferences.reload()}>
              Повторить загрузку настроек воронки
            </Button>
          )}
        </Stack>
      )}
      {(preferences.loading || (preferences.ready && loading)) && (
        <Group role='status' gap='xs'>
          <Loader size='xs' />
          <Text size='sm'>Загрузка лидов…</Text>
        </Group>
      )}
      {error && (
        <Stack gap='xs'>
          <Text role='alert' size='sm' c='red'>
            {error}
          </Text>
          <Button size='xs' variant='light' onClick={() => void controller.load(preferences.value)}>
            Повторить загрузку воронки
          </Button>
        </Stack>
      )}
      {data && (
        <>
          {data.types.length === 0 ? (
            <Text size='sm' c='dimmed'>
              Нет активных воронок лидов.
            </Text>
          ) : data.stages.length === 0 ? (
            <Text size='sm' c='dimmed'>
              В этой воронке нет статусов.
            </Text>
          ) : (
            <Stack gap='sm'>
              {data.total === 0 && (
                <Text size='sm' c='dimmed'>
                  В выбранной области пока нет лидов.
                </Text>
              )}
              {data.stages.map(stage => {
                const share = data.total > 0 ? (stage.count / data.total) * 100 : 0;
                return (
                  <Stack key={stage.id} gap={4}>
                    <Group justify='space-between' gap='xs' wrap='nowrap'>
                      <Text size='xs'>{stage.name}</Text>
                      <Text size='xs' fw={600} style={{ whiteSpace: 'nowrap' }}>
                        {count.format(stage.count)} · {percent.format(share)}%
                      </Text>
                    </Group>
                    <Progress
                      value={share}
                      color={stage.color || 'blue.6'}
                      size={8}
                      radius='xl'
                      aria-label={stage.name}
                    />
                  </Stack>
                );
              })}
              <Text size='xs' c='dimmed'>
                Всего лидов: {count.format(data.total)}
              </Text>
            </Stack>
          )}
        </>
      )}
    </WidgetFrame>
  );
}
